// CSV/TSV record render. Side-by-side is a key-aligned ledger: rows matched by
// key (sorted, so the reshuffle isn't churn), cells shown with changed values
// highlighted. Unified stacks original and changed records under shared columns; Raw is the line-based fallback.
import { inlineDiff } from '../engine/inline-diff.js';
import { esc } from './escape.js';
import { recordsUseTable } from '../lib/responsive.js';

const line = (n, t, cls = '') =>
  `<div class="code-line ${cls}"><span class="ln">${cls === 'del' ? '−' : cls === 'add' ? '+' : n}</span><span class="code">${t || ' '}</span></div>`;
const cap = (t) => `<div class="cap" style="margin-left:16px">${t}</div>`;
const fmt = (v) => (v === undefined || v === null ? '' : String(v));

// All records remain in the result model; only this many fixed-height ledger
// rows are mounted at once when the user opens a very large "All rows" view.
export const RECORD_VIRTUAL_THRESHOLD = 1_000;
export const RECORD_VIRTUAL_ROW_HEIGHT = 52;
export const RECORD_VIRTUAL_WINDOW = 40;

// A category filter narrows the ledger to one kind of change. One statement of
// the mapping, shared by both layouts, so the table and the stacked ledger can
// never disagree about what "added" shows. `null` means no filter.
const FILTER_STATUS = { added: 'add', removed: 'remove', changed: 'modify' };
export function filterRows(rows, filter) {
  if (!filter) return rows;
  if (filter === 'typeChanges') return rows.filter((r) => r.schemaChanged);
  if (filter === 'moved') return rows.filter((r) => r.reordered);
  const status = FILTER_STATUS[filter];
  return status ? rows.filter((r) => r.status === status) : rows;
}

export function renderRecords(result, view, meta = {}, opts = {}) {
  if (view === 'raw') {
    const rows = result.render.rawRows.map((r) => line(r.ln, esc(r.text), r.cls)).join('');
    return { html: rows + cap(result.format === 'parquet' ? 'Decoded JSON rows — this is a text representation, not the original Parquet bytes.' : 'Raw line view — shuffled rows read as deletes + adds, mostly false. Key matching removes the noise.'), ribbons: [] };
  }
  // recordsUseTable is the single statement of when a record view is a table —
  // shared with main.js and the QA responsive contract so no consumer restates
  // it (REL-063). Unified is always a table; Side-by-side only in table layout.
  if (recordsUseTable(view, opts.layout)) return { html: recordTable(result, { ...opts, unified: view === 'unified' }, meta), ribbons: [] };
  return { html: sideBySide(result, meta, opts), ribbons: [] };
}

export const TABLE_RECORD_HEIGHT = 80;

function tableColumns(result) {
  return result.render.columns.filter((name) => name !== result.render.keyColumn);
}

export function renderTableWindow(result, start = 0, size = RECORD_VIRTUAL_WINDOW, unified = false) {
  const columns = tableColumns(result);
  return result.render.rows.slice(start, start + size).map((row) => (unified ? unifiedRecord : tableRecord)(row, columns)).join('');
}

function reorderBadge(row) {
  return row.reordered ? `<small class="record-reorder" title="Row position in Original → Changed">↕ ${row.oldPosition} → ${row.newPosition}</small>` : '';
}

function recordStatus(row, side, glyph = '') {
  return `<span class="record-status-main"><span class="record-sign">${glyph}</span></span>${reorderBadge(row)}`;
}

function recordNumber(row, side, kind) {
  const position = side === 'old' ? row.oldPosition : row.newPosition;
  return `<td class="record-number record-${kind}${side === 'new' ? ' record-new' : ''}" title="${side === 'old' ? 'Original' : 'Changed'} data row">${kind === 'placeholder' ? '' : position ?? ''}</td>`;
}

function valueCell(cell, side, kind) {
  const absent = cell?.schema === (side === 'old' ? 'add' : 'remove');
  if (absent) return '<td class="record-placeholder" aria-label="Column absent"></td>';
  const changed = cell?.changed;
  const value = esc(fmt(side === 'old' ? cell?.oldValue : cell?.newValue));
  const content = changed ? inlineDiff(fmt(cell.oldValue), fmt(cell.newValue), 'word')
    .filter(part => part.type !== (side === 'old' ? 'new' : 'old'))
    .map(part => part.type === 'ctx' ? esc(part.text) : `<span class="record-inline-${side}">${esc(part.text)}</span>`).join('') : value;
  return `<td class="record-${cell?.schema || kind} ${changed ? `record-cell-${side}` : 'record-cell-plain'}"><span>${content}</span></td>`;
}

function tableRecord(row, columns) {
  const cells = new Map(row.cells.map((c) => [c.name, c]));
  const sideCells = (side) => {
    const absent = side === 'old' ? row.status === 'add' : row.status === 'remove';
    const kind = absent ? 'placeholder' : row.status;
    const glyph = absent || kind === 'context' ? '' : side === 'old' ? '−' : '+';
    const status = recordNumber(row, side, kind) + `<td class="record-status record-${kind}">${absent ? '' : recordStatus(row, side, glyph)}</td>`;
    if (absent) return status + `<td class="record-placeholder" colspan="${Math.max(1, columns.length) + 1}"></td>`;
    const values = columns.length ? columns.map((name) => {
      return valueCell(cells.get(name), side, kind);
    }).join('') : '<td class="record-cell-plain"></td>';
    return status + `<th scope="row" class="record-key record-${kind}">${esc(row.key)}</th>` + values;
  };
  // `data-key` is the address the change navigator resolves a record stop to.
  return `<tr class="record-pair${row.reordered ? ' record-reordered' : ''}" data-key="${esc(String(row.key))}" aria-label="${esc(row.key)}: ${row.status}">${sideCells('old')}${sideCells('new')}</tr>`;
}

function unifiedRecord(row, columns) {
  const cells = new Map(row.cells.map((cell) => [cell.name, cell]));
  const physicalRow = (side, kind, glyph) => {
    const values = columns.length ? columns.map((name) => {
          return valueCell(cells.get(name), side, kind);
        }).join('') : '<td></td>';
    return `<tr class="record-${kind}${row.reordered ? ' record-reordered' : ''}" data-key="${esc(String(row.key))}" aria-label="${kind === 'context' ? 'Unchanged' : side === 'old' ? 'Original' : 'Changed'} record ${esc(row.key)}">${recordNumber(row, side, kind)}<td class="record-status record-${kind}">${recordStatus(row, side, glyph)}</td><th scope="row" class="record-key">${esc(row.key)}</th>${values}</tr>`;
  };
  if (row.status === 'modify' || (row.status === 'context' && row.schemaChanged)) return physicalRow('old', 'modify', '−') + physicalRow('new', 'modify', '+');
  if (row.status === 'add') return physicalRow('new', 'add', '+');
  if (row.status === 'remove') return physicalRow('old', 'remove', '−');
  return physicalRow('new', 'context', '');
}

function recordTable(result, { scope = 'all', big = false, virtualize = true, unified = false, filter = null } = {}, meta = {}) {
  const rows = result.render.rows;
  const columns = tableColumns(result);
  const changed = rows.filter((row) => row.status !== 'context' || row.schemaChanged || row.reordered);
  // A filter is itself a narrowing of the changed set, so it implies that scope
  // rather than fighting it — and a filtered ledger is short, so it is never
  // virtualized (the window would hide rows the filter promised to show).
  const shown = filter ? filterRows(changed, filter) : scope === 'changed' ? changed : rows;
  const needsWrap = rows.some(row => row.cells.some(cell => [cell.oldValue, cell.newValue].some(value => /[\r\n]/.test(fmt(value)) || fmt(value).length > 32)));
  const virtual = !filter && scope === 'all' && virtualize && !needsWrap && rows.length > RECORD_VIRTUAL_THRESHOLD;
  const sideCount = Math.max(1, columns.length) + 1;
  const colCount = unified ? sideCount + 2 : (sideCount + 2) * 2;
  const sideCols = `<col class="record-number-col"><col class="record-status-col"><col class="record-key-col">${Array.from({ length: sideCount - 1 }, () => '<col class="record-value-col">').join('')}`;
  const sideHeaders = `<th scope="col" aria-label="Data row number">#</th><th scope="col" aria-label="Change status"></th><th scope="col">${esc(result.render.keyColumn || 'row')}</th>${columns.length ? columns.map((name) => `<th scope="col" data-column="${esc(name)}" class="${result.render.leftColumns && !result.render.leftColumns.includes(name) ? 'record-column-add' : result.render.rightColumns && !result.render.rightColumns.includes(name) ? 'record-column-remove' : ''}">${esc(name)}${result.render.leftColumns && !result.render.leftColumns.includes(name) ? ' +' : result.render.rightColumns && !result.render.rightColumns.includes(name) ? ' −' : ''}</th>`).join('') : '<th scope="col">Record</th>'}`;
  const spacer = (which, height) => `<tbody data-table-${which}><tr><td colspan="${colCount}" style="height:${height}px"></td></tr></tbody>`;
  const warning = result.warning === 'duplicate-key' ? '<div class="scope-note">⚠ Duplicate values in the key column — matched by <b>row position</b> instead, so a dropped row isn’t silently merged.</div>' : '';
  const body = virtual
    ? spacer('top', 0) + `<tbody data-table-window data-start="0">${renderTableWindow(result, 0, RECORD_VIRTUAL_WINDOW, unified)}</tbody>` + spacer('bottom', Math.max(0, rows.length - RECORD_VIRTUAL_WINDOW) * TABLE_RECORD_HEIGHT)
    : `<tbody>${shown.map((row) => (unified ? unifiedRecord : tableRecord)(row, columns)).join('')}</tbody>`;
  const keyHeader = `<th scope="col">${esc(result.render.keyColumn || 'row')}</th>`;
  const tableHead = unified
    ? `<colgroup><col class="record-number-col"><col class="record-status-col"><col class="record-key-col">${Array.from({ length: sideCount - 1 }, () => '<col class="record-value-col">').join('')}</colgroup><thead><tr><th scope="col" aria-label="Data row number">#</th><th scope="col" aria-label="Change status"></th>${keyHeader}${columns.length ? columns.map((name) => `<th scope="col" data-column="${esc(name)}" class="${result.render.leftColumns && !result.render.leftColumns.includes(name) ? 'record-column-add' : result.render.rightColumns && !result.render.rightColumns.includes(name) ? 'record-column-remove' : ''}">${esc(name)}${result.render.leftColumns && !result.render.leftColumns.includes(name) ? ' +' : result.render.rightColumns && !result.render.rightColumns.includes(name) ? ' −' : ''}</th>`).join('') : '<th scope="col">Record</th>'}</tr></thead>`
    : `<colgroup>${sideCols}${sideCols}</colgroup><thead><tr><th scope="colgroup" colspan="${sideCount + 2}">${esc(meta.leftLabel || 'Original')}</th><th scope="colgroup" colspan="${sideCount + 2}" class="record-side-heading">${esc(meta.rightLabel || 'Changed')}</th></tr><tr>${sideHeaders}${sideHeaders}</tr></thead>`;
  return warning + scopeLine({ filter, shown: shown.length, changed: changed.length, total: rows.length, big, scope }) +
    `<div data-table-layout="${unified ? 'unified' : 'sxs'}" class="record-table-scroll${virtual ? ' record-table-virtual' : ''}" tabindex="0" aria-label="Record comparison${virtual ? '; all rows available by scrolling' : ''}"><table class="record-table${unified ? ' record-table-unified' : ''}"${columns.length > 6 ? ` style="min-width:${colCount * 120}px"` : ''}>${tableHead}${body}</table></div>` + cap(alignmentCaption(result, result.render.keyColumn));
}

function cl(name, value, cls = '') {
  return `<span class="cl ${cls}">${esc(name)} <b>${esc(fmt(value))}</b></span>`;
}
function cellrow(cells, side, rowCls = '', highlight = false, extra = '') {
  const inner = cells
    .map((c) => cl(c.name, side === 'old' ? c.oldValue : c.newValue, highlight && c.changed ? 'chg' : ''))
    .join('');
  return `<span class="cellrow ${rowCls}">${inner}${extra}</span>`;
}

function sideBySide(result, meta, { scope = 'all', big = false, virtualize = true, filter = null } = {}) {
  const { columns, keyColumn, rows } = result.render;
  const keyLabel = keyColumn || 'row';
  const leftLabel = esc(meta.leftLabel || 'Original');
  const rightLabel = esc(meta.rightLabel || 'Changed');

  // Compute everything, then choose what to SHOW — never truncate (spec §8.5).
  const changed = rows.filter((r) => r.status !== 'context');
  const shown = filter ? filterRows(changed, filter) : scope === 'changed' ? changed : rows;
  const scopeN = scopeLine({ filter, shown: shown.length, changed: changed.length, total: rows.length, big, scope });
  const warn = result.warning === 'duplicate-key' ? `<div class="scope-note">⚠ Duplicate values in the key column — matched by <b>row position</b> instead, so a dropped row isn’t silently merged.</div>` : '';
  const heading = recordHeading(keyLabel, leftLabel, rightLabel);
  const caption = alignmentCaption(result, keyColumn);

  if (!filter && scope === 'all' && virtualize && rows.length > RECORD_VIRTUAL_THRESHOLD) {
    const initial = renderRecordWindow(result, 0);
    return `${warn}${scopeN}<div class="record-virtual" data-record-virtual data-total="${rows.length}" data-row-height="${RECORD_VIRTUAL_ROW_HEIGHT}">${heading}<div class="record-virtual-viewport" tabindex="0" aria-label="All ${rows.length} records; rows load as you scroll"><div class="record-virtual-spacer" style="height:${rows.length * RECORD_VIRTUAL_ROW_HEIGHT}px"></div><div class="sxs record-virtual-window" data-start="0" style="grid-template-columns:72px 1fr 34px 1fr;transform:translateY(0px)">${initial.html}</div></div></div>${cap(caption)}`;
  }

  let h = warn + scopeN + `<div class="sxs" style="grid-template-columns:72px 1fr 34px 1fr">${headingBody(keyLabel, leftLabel, rightLabel)}`;
  h += shown.map((row) => recordRow(row, keyLabel)).join('');
  h += cap(caption);
  return `${h}</div>`;
}

function recordHeading(keyLabel, leftLabel, rightLabel) {
  return `<div class="sxs record-virtual-head" style="grid-template-columns:72px 1fr 34px 1fr">${headingBody(keyLabel, leftLabel, rightLabel)}</div>`;
}

function headingBody(keyLabel, leftLabel, rightLabel) {
  return `<div class="sxs-hdr" style="grid-template-columns:subgrid"><span class="h">${esc(keyLabel)}</span><span class="h">${leftLabel}</span><span></span><span class="h">${rightLabel}</span></div>`;
}

function alignmentCaption(result, keyColumn) {
  return result.warning === 'duplicate-key'
    ? 'Rows aligned by position (duplicate keys detected) — so a dropped row isn’t silently merged.'
    : `Rows aligned by key <b>${esc(keyColumn)}</b>, not by position — so the reshuffled file reads like a ledger and only real edits are highlighted.`;
}

function recordRow(row, keyLabel) {
  const spacer = (t) => `<span class="c sp" style="margin:3px 0;border-radius:6px"> ${t} </span>`;
  const keyc = `<span class="keyc">${esc(keyLabel)} ${esc(row.key)}</span>`;
  let left;
  let right;
  if (row.status === 'add') {
    left = spacer('not present');
    right = cellrow(row.cells, 'new', 'rowadd');
  } else if (row.status === 'remove') {
    left = cellrow(row.cells, 'old', 'rowdel');
    right = spacer('removed');
  } else {
    // context (incl. reordered-but-identical): shown plain, never tagged (§8.4)
    left = cellrow(row.cells, 'old');
    right = cellrow(row.cells, 'new', '', row.status === 'modify');
  }
  // Row colour wherever there is a change: the ledger used to tint only the
  // changed CHIP, so the same comparison read as rows in the table and as
  // specks here. `modify` covers a schema-only difference too.
  const stat = row.status === 'add' ? ' rowstat-add'
    : row.status === 'remove' ? ' rowstat-remove'
      : row.status === 'modify' || row.schemaChanged ? ' rowstat-modify' : '';
  return `<div class="sxs-row${stat}" style="grid-template-columns:subgrid" data-key="${esc(String(row.key))}">${keyc}${left}<span class="gut"></span>${right}</div>`;
}

/** Render one bounded window without losing the complete result model. */
export function renderRecordWindow(result, start = 0, size = RECORD_VIRTUAL_WINDOW) {
  const rows = result.render?.rows || [];
  const count = Math.max(1, Math.floor(Number(size) || RECORD_VIRTUAL_WINDOW));
  const maxStart = Math.max(0, rows.length - count);
  const from = Math.min(maxStart, Math.max(0, Math.floor(Number(start) || 0)));
  const end = Math.min(rows.length, from + count);
  const keyLabel = result.render?.keyColumn || 'row';
  return { html: rows.slice(from, end).map((row) => recordRow(row, keyLabel)).join(''), start: from, end };
}

// One line under the toolbar stating exactly what is on screen. A filter always
// says so — hiding changes without a visible reason is the thing spec §8.5
// forbids, and the reader needs the way back.
function scopeLine({ filter, shown, changed, total, big, scope }) {
  if (filter) {
    const hidden = changed - shown;
    return `<div class="scope-note">Showing <b>${shown}</b> of ${changed} affected ${changed === 1 ? 'record' : 'records'}${hidden > 0 ? ` — ${hidden} other affected ${hidden === 1 ? 'record is' : 'records are'} hidden by this filter` : ''}. Nothing was truncated; use <b>Show all changes</b> to see them.</div>`;
  }
  return big ? scopeNote(scope, changed, total) : '';
}

// Count line for large record sets — states exactly what's shown vs. hidden and
// that nothing was dropped (spec §8.5: silent truncation is prohibited).
function scopeNote(scope, changedCount, total) {
  const body =
    scope === 'changed'
      ? `Showing <b>${changedCount} affected</b> ${changedCount === 1 ? 'record' : 'records'} of ${total} — added, removed and modified. The ${total - changedCount} identical rows are hidden; nothing was truncated or skipped, switch to <b>All rows</b> to see them.`
      : `Showing <b>all ${total} rows</b>.`;
  return `<div class="scope-note">${body}</div>`;
}
